import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import collectionSlugs from '../data/collectionSlugs';
import '../styles/CollectionDetail.css';

const TIMEFRAMES = [
  { label: '7D',  days: 7  },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: 'All', days: 0  },
];

// Inline transform matching CollectionsTable (used when falling back to localStorage)
const getCategoryMapping = () => {
  const mapping = {};
  collectionSlugs.forEach((cat) => {
    cat.slugs.forEach((slug) => { mapping[slug] = cat.category; });
  });
  return mapping;
};

const transformOne = (collection) => {
  const categoryMapping = getCategoryMapping();
  const floorPrice = parseFloat(collection.stats?.total?.floor_price || 0);
  const totalSupply = parseInt(collection.total_supply || 0, 10);
  const marketCap = floorPrice * totalSupply;

  return {
    category: categoryMapping[collection.collection] || 'Uncategorized',
    collection: collection.name || 'Unknown Collection',
    imageUrl: collection.image_url || '',
    totalOwners: parseInt(collection.stats?.total?.num_owners || 0, 10),
    totalSupply,
    totalVolume: parseFloat(collection.stats?.total?.volume || 0),
    marketCap,
    floorPrice,
    contractAddress: collection.contracts?.[0]?.address || 'N/A',
    chain: collection.contracts?.[0]?.chain || 'Unknown',
    openseaUrl: collection.opensea_url || '#',
    slug: collection.collection || '',
    description: collection.description || null,
    twitterUsername: collection.twitter_username || null,
    websiteUrl: collection.website_url || null,
    discordUrl: collection.discord_url || null,
    bannerImageUrl: collection.banner_image_url || null,
  };
};

// Group sale events into daily average prices
const groupByDay = (points, days) => {
  // Client-side cutoff to enforce the selected window precisely
  const cutoff = days > 0
    ? new Date(Date.now() - days * 86400 * 1000).toISOString()
    : null;

  const filtered = cutoff ? points.filter((p) => p.timestamp >= cutoff) : points;

  const buckets = {};
  filtered.forEach(({ timestamp, price }) => {
    const day = timestamp.slice(0, 10);
    if (!buckets[day]) buckets[day] = { sum: 0, count: 0 };
    buckets[day].sum += price;
    buckets[day].count++;
  });
  return Object.entries(buckets)
    .map(([date, { sum, count }]) => ({ date, avgPrice: +(sum / count).toFixed(4) }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

const formatCrypto = (val, symbol = 'ETH') => {
  if (!val || isNaN(val) || val === 0) return '—';
  return `${parseFloat(val).toFixed(3)} ${symbol}`;
};

const formatUSD = (ethValue, price) => {
  if (!price || typeof ethValue !== 'number' || isNaN(ethValue) || ethValue === 0) return null;
  const usd = ethValue * price;
  if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
  if (usd >= 1e6) return `$${(usd / 1e6).toFixed(2)}M`;
  if (usd >= 1e3) return `$${(usd / 1e3).toFixed(1)}K`;
  return `$${usd.toFixed(2)}`;
};

const formatCount = (num) => {
  if (typeof num !== 'number' || isNaN(num) || num === 0) return '—';
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString();
};

// Custom tooltip — currency-aware
const ChartTooltip = ({ active, payload, label, symbol }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-date">{label}</div>
      <div className="chart-tooltip-value">{payload[0].value} {symbol}</div>
    </div>
  );
};

const CollectionDetail = () => {
  const { slug } = useParams();
  const location = useLocation();

  const [collection, setCollection] = useState(null);
  const [rawEvents, setRawEvents] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(0);
  const [ethPrice, setEthPrice] = useState(null);

  // ── Floor alerts ──
  const [alerts, setAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('alerts_v1') || '[]'); }
    catch { return []; }
  });
  const [alertDir,       setAlertDir]       = useState('below');
  const [alertThreshold, setAlertThreshold] = useState('');

  // Two-tier data loading: React Router state → localStorage cache → not found
  useEffect(() => {
    if (location.state && location.state.slug === slug) {
      setCollection(location.state);
      return;
    }

    const cached = localStorage.getItem('collections_cache_v2');
    if (cached) {
      try {
        const collections = JSON.parse(cached);
        const found = collections.find((c) => c.collection === slug);
        if (found) { setCollection(transformOne(found)); return; }
      } catch (_) {}
    }

    setCollection(null);
  }, [slug, location.state]);

  // Fetch ETH price
  useEffect(() => {
    fetch('/api/eth-price')
      .then((r) => r.json())
      .then((d) => setEthPrice(d.usd))
      .catch(() => {});
  }, []);

  // Fetch sale events — re-fetch when timeframe changes; cache per slug+days in sessionStorage
  useEffect(() => {
    const cacheKey = `chart_${slug}_${selectedDays}`;
    const CACHE_TTL = 5 * 60 * 1000;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL && Array.isArray(data)) {
          setRawEvents(data);
          setChartLoading(false);
          return;
        }
      } catch (_) {}
    }

    setChartLoading(true);
    fetch(`/api/collection-events?slug=${encodeURIComponent(slug)}&days=${selectedDays}`)
      .then((r) => r.json())
      .then((points) => {
        if (Array.isArray(points)) {
          setRawEvents(points);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ data: points, ts: Date.now() }));
          } catch (_) {}
        }
      })
      .catch(() => {})
      .finally(() => setChartLoading(false));
  }, [slug, selectedDays]);

  // Re-compute chart data when raw events or selected timeframe changes
  useEffect(() => {
    setChartData(groupByDay(rawEvents, selectedDays));
  }, [rawEvents, selectedDays]);

  const currentAlert = alerts.find((a) => a.slug === slug);

  const handleSetAlert = () => {
    if (!alertThreshold || !collection) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const newAlert = {
      slug,
      name:      collection.collection,
      threshold: parseFloat(alertThreshold),
      direction: alertDir,
      imageUrl:  collection.imageUrl,
    };
    const next = [...alerts.filter((a) => a.slug !== slug), newAlert];
    localStorage.setItem('alerts_v1', JSON.stringify(next));
    setAlerts(next);
    setAlertThreshold('');
  };

  const handleRemoveAlert = () => {
    const next = alerts.filter((a) => a.slug !== slug);
    localStorage.setItem('alerts_v1', JSON.stringify(next));
    setAlerts(next);
  };

  if (collection === null && !location.state) {
    return (
      <div className="container">
        <p className="detail-not-found">
          Collection not found. <Link to="/">← Back to collections</Link>
        </p>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="container">
        <p className="detail-loading">Loading…</p>
      </div>
    );
  }

  const cryptoSymbol = collection.chain === 'matic' ? 'MATIC' : 'ETH';
  const yAxisTick = cryptoSymbol === 'ETH' ? 'Ξ' : 'MATIC';

  const etherscanBase = collection.chain === 'ethereum'
    ? 'https://etherscan.io/address/'
    : 'https://explorer-mainnet.maticvigil.com/address/';

  const selectedLabel = TIMEFRAMES.find((t) => t.days === selectedDays)?.label ?? '30D';

  return (
    <div className="container">
      <Helmet>
        <title>{collection.collection} — Mint Debut</title>
        <meta name="description" content={collection.description || `${collection.collection} genesis NFT collection stats.`} />
        <meta property="og:title" content={`${collection.collection} — Mint Debut`} />
        <meta property="og:image" content={collection.imageUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image" content={collection.imageUrl} />
      </Helmet>

      <div className="detail-back">
        <Link to="/">← Collections</Link>
      </div>

      {collection.bannerImageUrl && (
        <div
          className="detail-banner"
          style={{ backgroundImage: `url(${collection.bannerImageUrl})` }}
        />
      )}

      <div className="detail-header">
        <img className="detail-avatar" src={collection.imageUrl} alt={collection.collection} />
        <div className="detail-header-text">
          <span className="detail-category">{collection.category}</span>
          <h1 className="detail-name">{collection.collection}</h1>
        </div>
      </div>

      {collection.description && (
        <p className="detail-description">{collection.description}</p>
      )}

      <div className="detail-stats-strip">
        <div className="detail-stat">
          <span>Floor</span>
          <strong>{formatCrypto(collection.floorPrice, cryptoSymbol)}</strong>
          {ethPrice && cryptoSymbol === 'ETH' && <em>{formatUSD(collection.floorPrice, ethPrice)}</em>}
        </div>
        <div className="detail-stat">
          <span>Total Volume</span>
          <strong>{formatCrypto(collection.totalVolume, cryptoSymbol)}</strong>
          {ethPrice && cryptoSymbol === 'ETH' && <em>{formatUSD(collection.totalVolume, ethPrice)}</em>}
        </div>
        <div className="detail-stat">
          <span>Market Cap</span>
          <strong>
            {ethPrice && cryptoSymbol === 'ETH'
              ? formatUSD(collection.marketCap, ethPrice)
              : formatCrypto(collection.marketCap, cryptoSymbol)}
          </strong>
        </div>
        <div className="detail-stat">
          <span>Owners</span>
          <strong>{formatCount(collection.totalOwners)}</strong>
        </div>
        <div className="detail-stat">
          <span>Supply</span>
          <strong>{formatCount(collection.totalSupply)}</strong>
        </div>
      </div>

      <div className="detail-chart-section">
        <div className="detail-chart-header">
          <h2 className="detail-section-title">
            Avg Sale Price ({cryptoSymbol})
          </h2>
          <div className="chart-timeframes">
            {TIMEFRAMES.map(({ label, days }) => (
              <button
                key={label}
                className={selectedDays === days ? 'active' : ''}
                onClick={() => setSelectedDays(days)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {chartLoading ? (
          <div className="detail-chart-placeholder">Loading chart…</div>
        ) : chartData.length === 0 ? (
          <div className="detail-chart-placeholder">
            No sales data for the {selectedLabel} period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="amberGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF9C00" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#FF9C00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2C" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#4D4D4D', fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: '#2C2C2C' }}
              />
              <YAxis
                tick={{ fill: '#4D4D4D', fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v} ${yAxisTick}`}
                width={cryptoSymbol === 'ETH' ? 55 : 80}
              />
              <Tooltip content={<ChartTooltip symbol={cryptoSymbol} />} />
              <Area
                type="monotone"
                dataKey="avgPrice"
                stroke="#FF9C00"
                strokeWidth={2}
                fill="url(#amberGradient)"
                dot={false}
                activeDot={{ r: 4, fill: '#FF9C00' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="detail-links">
        <a href={collection.openseaUrl} target="_blank" rel="noopener noreferrer">OpenSea</a>
        {collection.contractAddress && collection.contractAddress !== 'N/A' && (
          <a href={`${etherscanBase}${collection.contractAddress}`} target="_blank" rel="noopener noreferrer">
            {collection.chain === 'ethereum' ? 'Etherscan' : 'Explorer'}
          </a>
        )}
        {collection.websiteUrl && (
          <a href={collection.websiteUrl} target="_blank" rel="noopener noreferrer">Website</a>
        )}
        {collection.twitterUsername && (
          <a href={`https://twitter.com/${collection.twitterUsername}`} target="_blank" rel="noopener noreferrer">Twitter</a>
        )}
        {collection.discordUrl && (
          <a href={collection.discordUrl} target="_blank" rel="noopener noreferrer">Discord</a>
        )}
      </div>

      <div className="detail-alert-section">
        <h2 className="detail-section-title" style={{ marginBottom: '12px' }}>Floor Alert</h2>
        {currentAlert ? (
          <div className="alert-active">
            <span>🔔 Notify when floor {currentAlert.direction} {currentAlert.threshold} ETH</span>
            <button onClick={handleRemoveAlert}>Remove</button>
          </div>
        ) : (
          <div className="alert-form">
            <select value={alertDir} onChange={(e) => setAlertDir(e.target.value)}>
              <option value="below">Below</option>
              <option value="above">Above</option>
            </select>
            <input
              type="number"
              value={alertThreshold}
              onChange={(e) => setAlertThreshold(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
            <span className="alert-unit">ETH</span>
            <button onClick={handleSetAlert} disabled={!alertThreshold}>Set Alert</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDetail;
